import React, { useState, useEffect, useCallback } from 'react';
import { PieChart, Pie, Cell, Tooltip, ResponsiveContainer, Treemap } from 'recharts';
import {
  Plus, Edit2, Trash2, Target, TrendingUp, TrendingDown,
  Check, Star, Wallet, CreditCard, Activity, ChevronDown, ChevronUp,
} from 'lucide-react';
import { useAppState } from '../store/AppContext';
import { Card, CardBody } from './ui/Card';
import { Button } from './ui/Button';
import { Modal } from './ui/Modal';
import { Input, Textarea } from './ui/Input';
import { Bucket } from '../types';
import {
  getBucketProgress, getAssetCurrentValue,
  getTotalLiabilities, getTotalNAV, CASH_WALLET_ASSET_ID,
} from '../utils/calculations';
import { formatVND } from '../utils/format';
import { Money, SensitiveValue } from './ui/SensitiveValue';
import { supabase } from '../lib/supabaseClient';

// ── Types ──────────────────────────────────────────────────────────────────────
interface FundOverview {
  id: string;
  name: string;
  description: string;
  color: string;
  target_value: number;
  current_nav: number;
  completion_status: 'constructing' | 'completed' | 'over';
  variance: number;
  yield_rate: number;
  cap_gain_rate: number;
  total_return_rate: number;
}

type FundStatus = 'constructing' | 'completed' | 'over';

// ── Helpers ────────────────────────────────────────────────────────────────────
const COLORS_FALLBACK = ['#10b981','#38bdf8','#fbbf24','#a78bfa','#f87171','#f97316','#06b6d4','#84cc16','#94a3b8'];
const BUCKET_FORM_COLORS = ['#10b981','#38bdf8','#fbbf24','#a78bfa','#f87171','#f97316','#06b6d4','#84cc16'];
const empty = (): Partial<Bucket> => ({ name: '', description: '', targetValue: 0, color: '#10b981', icon: 'target' });

function getStatusFromBucket(b: Bucket, assets: any[]): FundStatus {
  if (b.targetValue <= 0) return 'constructing';
  const { nav } = getBucketProgress(b, assets);
  if (nav > b.targetValue) return 'over';
  if (nav >= b.targetValue) return 'completed';
  return 'constructing';
}

// ── KPI Card ──────────────────────────────────────────────────────────────────
function KpiCard({ icon, label, value, sub, color, accentBg }: {
  icon: React.ReactNode; label: string; value: React.ReactNode;
  sub?: string; color: string; accentBg?: string;
}) {
  return (
    <div style={{
      flex: 1, minWidth: 0, borderRadius: 16, padding: '20px 22px',
      background: accentBg || 'var(--bg-card)', border: '1px solid var(--bd)',
      position: 'relative', overflow: 'hidden',
    }}>
      <div style={{
        position: 'absolute', top: -16, right: -16, width: 72, height: 72,
        borderRadius: '50%', background: `${color}15`,
      }} />
      <div style={{ display: 'flex', alignItems: 'center', gap: 8, marginBottom: 12 }}>
        <div style={{
          width: 32, height: 32, borderRadius: 9, background: `${color}18`,
          border: `1px solid ${color}28`, display: 'flex', alignItems: 'center', justifyContent: 'center',
        }}>
          {icon}
        </div>
        <span style={{ fontSize: 10, fontWeight: 700, textTransform: 'uppercase', letterSpacing: '0.07em', color: 'var(--tx-4)' }}>
          {label}
        </span>
      </div>
      <div style={{ fontSize: 26, fontWeight: 700, color, letterSpacing: '-0.03em', lineHeight: 1 }}>{value}</div>
      {sub && <div style={{ fontSize: 11, color: 'var(--tx-4)', marginTop: 6 }}>{sub}</div>}
    </div>
  );
}

// ── Kanban Column ──────────────────────────────────────────────────────────────
function KanbanColumn({
  label, color, bgOverlay, borderColor, icon, buckets, status, assets, liabilities, totalNAV, overviewRows,
  onNavigateToFund, onNavigateToFire, onEdit, onDelete, expanded, setExpanded,
}: {
  label: string; color: string; bgOverlay: string; borderColor: string;
  icon: React.ReactNode; buckets: Bucket[]; status: FundStatus; assets: any[]; liabilities: any[]; totalNAV: number;
  overviewRows: FundOverview[];
  onNavigateToFund?: (id: string) => void; onNavigateToFire?: (id: string) => void;
  onEdit: (b: Bucket) => void; onDelete: (b: Bucket) => void;
  expanded: string | null; setExpanded: (id: string | null) => void;
}) {
  const colNAV = buckets.reduce((s, b) => {
    const gross    = getBucketProgress(b, assets).nav;
    const fundDebt = liabilities.filter((l: any) => l.fundId === b.id).reduce((ds: number, l: any) => ds + l.outstandingBalance, 0);
    return s + Math.max(0, gross - fundDebt);
  }, 0);
  return (
    <div style={{
      borderRadius: 16, background: bgOverlay, border: `1px solid ${borderColor}`,
      display: 'flex', flexDirection: 'column',
    }}>
      {/* Column header */}
      <div style={{ padding: '16px 16px 14px', borderBottom: `1px solid ${borderColor}` }}>
        <div style={{ display: 'flex', alignItems: 'center', gap: 8, marginBottom: 10 }}>
          {icon}
          <span style={{ fontSize: 11, fontWeight: 800, color, textTransform: 'uppercase', letterSpacing: '0.08em', flex: 1 }}>
            {label}
          </span>
          <span style={{ fontSize: 10, fontWeight: 700, padding: '2px 9px', borderRadius: 10, background: `${color}20`, color, border: `1px solid ${color}30` }}>
            {buckets.length} quỹ
          </span>
        </div>
        <div style={{ fontSize: 20, fontWeight: 700, color: 'var(--tx-1)', letterSpacing: '-0.03em', lineHeight: 1 }}>
          <Money value={colNAV} />
        </div>
        <div style={{ fontSize: 10, color: 'var(--tx-5)', marginTop: 3 }}>Tổng NAV</div>
      </div>
      {/* Cards */}
      <div style={{ display: 'flex', flexDirection: 'column', gap: 10, padding: 12, flex: 1 }}>
        {buckets.map(b => (
          <FundCard key={b.id} bucket={b} status={status} assets={assets} liabilities={liabilities} totalNAV={totalNAV}
            overviewRow={overviewRows.find(r => r.id === b.id)}
            onNavigateToFund={onNavigateToFund} onNavigateToFire={onNavigateToFire}
            onEdit={onEdit} onDelete={onDelete} expanded={expanded} setExpanded={setExpanded} />
        ))}
        {buckets.length === 0 && (
          <div style={{ flex: 1, display: 'flex', alignItems: 'center', justifyContent: 'center', minHeight: 80, color: 'var(--tx-5)', fontSize: 12 }}>
            Không có quỹ nào
          </div>
        )}
      </div>
    </div>
  );
}

// ── Fund Card ─────────────────────────────────────────────────────────────────
function FundCard({
  bucket: b, status, assets, liabilities, totalNAV, overviewRow,
  onNavigateToFund, onNavigateToFire, onEdit, onDelete,
  expanded, setExpanded,
}: {
  bucket: Bucket; status: FundStatus; assets: any[]; liabilities: any[]; totalNAV: number;
  overviewRow?: FundOverview;
  onNavigateToFund?: (id: string) => void; onNavigateToFire?: (id: string) => void;
  onEdit: (b: Bucket) => void; onDelete: (b: Bucket) => void;
  expanded: string | null; setExpanded: (id: string | null) => void;
}) {
  const { nav: grossNAV } = getBucketProgress(b, assets);
  const fundDebt    = liabilities.filter((l: any) => l.fundId === b.id).reduce((s: number, l: any) => s + l.outstandingBalance, 0);
  const nav         = grossNAV - fundDebt;
  const yieldRate   = overviewRow?.yield_rate      ?? 0;
  const capGainRate = overviewRow?.cap_gain_rate   ?? 0;
  const totalReturn = overviewRow?.total_return_rate ?? 0;
  const pct         = b.targetValue > 0 ? (nav / b.targetValue) * 100 : null;
  const diff        = b.targetValue > 0 ? nav - b.targetValue : null;
  const bucketAssets = assets.filter((a: any) => a.bucketId === b.id);
  const isExpanded = expanded === b.id;

  const statusBadge = {
    constructing: null,
    completed: (
      <span style={{ display:'inline-flex', alignItems:'center', gap:4, fontSize:10, fontWeight:700, padding:'2px 8px', borderRadius:8, background:'rgba(16,185,129,0.12)', border:'1px solid rgba(16,185,129,0.25)', color:'#10b981' }}>
        <Check size={9} /> Hoàn thành
      </span>
    ),
    over: (
      <span style={{ display:'inline-flex', alignItems:'center', gap:4, fontSize:10, fontWeight:700, padding:'2px 8px', borderRadius:8, background:'rgba(167,139,250,0.12)', border:'1px solid rgba(167,139,250,0.25)', color:'#a78bfa' }}>
        ★ Thặng dư
      </span>
    ),
  }[status];

  return (
    <Card onClick={() => onNavigateToFund?.(b.id)}>
      <div style={{ padding: '18px 20px' }}>
        {/* Header */}
        <div style={{ display:'flex', alignItems:'flex-start', justifyContent:'space-between', marginBottom: 10 }}>
          <div style={{ display:'flex', alignItems:'center', gap:11 }}>
            <div style={{
              width:36, height:36, borderRadius:10, flexShrink:0,
              background:`linear-gradient(135deg,${b.color}25,${b.color}10)`,
              border:`1px solid ${b.color}30`,
              display:'flex', alignItems:'center', justifyContent:'center',
            }}>
              {status === 'completed' || status === 'over'
                ? <Check size={15} style={{ color: status === 'over' ? '#a78bfa' : '#10b981' }} />
                : <Target size={15} style={{ color: b.color }} />
              }
            </div>
            <div>
              <div style={{ display:'flex', alignItems:'center', gap:7, flexWrap:'wrap' }}>
                <span style={{ fontSize:14, fontWeight:700, color:'var(--tx-1)' }}>{b.name}</span>
                {statusBadge}
              </div>
            </div>
          </div>
          {/* Actions — stopPropagation */}
          <div style={{ display:'flex', gap:3, flexShrink:0 }}>
            <MiniBtn icon={<Edit2 size={11}/>} color="var(--tx-4)" title="Sửa" onClick={e=>{ e.stopPropagation(); onEdit(b); }} />
            <MiniBtn icon={<Trash2 size={11}/>} color="#f87171" title="Xóa" onClick={e=>{ e.stopPropagation(); onDelete(b); }} />
          </div>
        </div>

        {/* Description — sứ mệnh quỹ */}
        {b.description && (
          <div style={{ fontSize:11, color:'var(--tx-4)', fontStyle:'italic', marginBottom:14, lineHeight:1.5, paddingLeft:1 }}>
            {b.description}
          </div>
        )}

        {/* NAV (net) */}
        <div style={{ marginBottom:14 }}>
          <div style={{ fontSize:24, fontWeight:700, color:'var(--tx-1)', letterSpacing:'-0.03em', lineHeight:1 }}>
            <Money value={nav} />
          </div>
          <div style={{ fontSize:11, color:'var(--tx-4)', marginTop:4 }}>
            {bucketAssets.length} tài sản · {totalNAV > 0 ? ((Math.max(0,nav)/totalNAV)*100).toFixed(1) : 0}% tổng gia sản
          </div>
          <div style={{ fontSize:11, color:'var(--tx-5)', marginTop:3, visibility: fundDebt > 0 ? 'visible' : 'hidden' }}>
            Tài sản: <span style={{ color:'var(--tx-3)' }}><Money value={grossNAV} /></span>
            {' · '}
            Nợ: <span style={{ color:'#f87171' }}><Money value={fundDebt} /></span>
          </div>
        </div>

        {/* Performance metrics */}
        <div style={{ display:'flex', gap:6, marginBottom:14, flexWrap:'wrap' }}>
          <MetricPill
            label="Y"
            value={yieldRate}
            color="#fbbf24"
            tooltip={yieldRate > 0 ? `Thu nhập: ${formatVND(nav * yieldRate / 100)}/năm` : 'Chưa có dữ liệu lợi suất'}
          />
          <MetricPill
            label="G"
            value={capGainRate}
            color="#38bdf8"
            tooltip={capGainRate > 0 ? `Tăng giá: ${formatVND(nav * capGainRate / 100)}/năm` : 'Chưa có dữ liệu tăng trưởng'}
          />
          <MetricPill
            label="Σ"
            value={totalReturn}
            color="#10b981"
            tooltip={totalReturn > 0 ? `Tổng lợi nhuận: ${formatVND(nav * totalReturn / 100)}/năm` : 'Chưa có dữ liệu tổng lợi nhuận'}
          />
        </div>

        {/* Progress */}
        {b.targetValue > 0 && (
          <div>
            <div style={{ display:'flex', justifyContent:'space-between', alignItems:'center', marginBottom:6 }}>
              {status === 'constructing' && (
                <div style={{ display:'flex', alignItems:'center', gap:5 }}>
                  <TrendingDown size={12} color="#fbbf24" />
                  <span style={{ fontSize:12, fontWeight:700, color:'#fbbf24' }}>Còn thiếu <Money value={Math.abs(diff ?? 0)} /></span>
                </div>
              )}
              {status === 'completed' && (
                <div style={{ display:'flex', alignItems:'center', gap:5 }}>
                  <Check size={12} color="#10b981" />
                  <span style={{ fontSize:12, fontWeight:700, color:'#10b981' }}>Đã đạt mục tiêu</span>
                </div>
              )}
              {status === 'over' && (
                <div style={{ display:'flex', alignItems:'center', gap:5 }}>
                  <TrendingUp size={12} color="#a78bfa" />
                  <span style={{ fontSize:12, fontWeight:700, color:'#a78bfa' }}>Vượt định mức <Money value={Math.abs(diff ?? 0)} /></span>
                </div>
              )}
              <span style={{ fontSize:10, color:'var(--tx-5)' }}>MT: <Money value={b.targetValue} /></span>
            </div>
            <div style={{ height:5, borderRadius:3, backgroundColor:'var(--bd)', overflow:'hidden' }}>
              <div style={{
                height:'100%', borderRadius:3, transition:'width 0.6s ease',
                width:`${Math.min(pct ?? 0, 130)}%`,
                background: status === 'over'
                  ? 'linear-gradient(90deg,#a78bfa,#7c3aed)'
                  : status === 'completed'
                    ? 'linear-gradient(90deg,#10b981,#059669)'
                    : (pct ?? 0) > 70
                      ? `linear-gradient(90deg,${b.color},${b.color}aa)`
                      : 'linear-gradient(90deg,#fbbf24,#f59e0b)',
              }} />
            </div>
            <div style={{ textAlign:'right', marginTop:3, fontSize:10, color:'var(--tx-5)' }}>
              {(pct ?? 0).toFixed(1)}%
            </div>
          </div>
        )}
      </div>

      {/* Asset list toggle */}
      {bucketAssets.length > 0 && (
        <>
          <button
            onClick={e=>{ e.stopPropagation(); setExpanded(isExpanded ? null : b.id); }}
            style={{ width:'100%', padding:'8px 20px', display:'flex', alignItems:'center', justifyContent:'space-between', cursor:'pointer', background:'none', border:'none', borderTop:'1px solid var(--bg-card-el)', color:'var(--tx-5)', fontSize:11 }}
            onMouseEnter={e=>{(e.currentTarget as HTMLButtonElement).style.background='var(--bg-row-hover)';}}
            onMouseLeave={e=>{(e.currentTarget as HTMLButtonElement).style.background='none';}}
          >
            <span>{bucketAssets.length} tài sản</span>
            {isExpanded ? <ChevronUp size={12}/> : <ChevronDown size={12}/>}
          </button>
          {isExpanded && (
            <div style={{ borderTop:'1px solid var(--bd-sub)' }} onClick={e=>e.stopPropagation()}>
              {bucketAssets.map((a:any, i:number) => (
                <div key={a.id} style={{ display:'flex', alignItems:'center', justifyContent:'space-between', padding:'9px 20px', borderBottom: i < bucketAssets.length-1 ? '1px solid var(--bd-sub)' : 'none' }}>
                  <div style={{ display:'flex', alignItems:'center', gap:8 }}>
                    <div style={{ width:4, height:4, borderRadius:'50%', backgroundColor:b.color }} />
                    <span style={{ fontSize:12, fontWeight:600, color:'var(--tx-2)', maxWidth:160, overflow:'hidden', textOverflow:'ellipsis', whiteSpace:'nowrap' }}>{a.name}</span>
                  </div>
                  <span style={{ fontSize:12, color:'var(--tx-3)', fontWeight:600 }}><Money value={getAssetCurrentValue(a)} /></span>
                </div>
              ))}
            </div>
          )}
        </>
      )}
    </Card>
  );
}

function MiniBtn({ icon, color, title, onClick }: {
  icon: React.ReactNode; color: string; title?: string; onClick: (e: React.MouseEvent) => void;
}) {
  return (
    <button onClick={onClick} title={title}
      style={{ width:26, height:26, borderRadius:6, display:'flex', alignItems:'center', justifyContent:'center', cursor:'pointer', background:'var(--bg-card-el)', border:'none', color, transition:'background 0.1s' }}
      onMouseEnter={e=>{(e.currentTarget as HTMLButtonElement).style.background='var(--bg-row-hover)';}}
      onMouseLeave={e=>{(e.currentTarget as HTMLButtonElement).style.background='var(--bg-card-el)';}}
    >{icon}</button>
  );
}

// ── Metric Pill ─────────────────────────────────────────────────────────────
function MetricPill({ label, value, color, tooltip }: {
  label: string; value: number; color: string; tooltip: string;
}) {
  const dim = value === 0;
  return (
    <div title={tooltip} style={{
      position: 'relative',
      display: 'inline-flex', alignItems: 'center', gap: 4,
      padding: '3px 8px', borderRadius: 7,
      background: dim ? 'var(--bg-card-el)' : `${color}15`,
      border: `1px solid ${dim ? 'var(--bd)' : `${color}30`}`,
      cursor: 'default',
    }}>
      <span style={{ fontSize: 9, fontWeight: 800, color: dim ? 'var(--tx-5)' : color, letterSpacing: '0.04em' }}>
        {label}
      </span>
      <span style={{ fontSize: 11, fontWeight: 700, color: dim ? 'var(--tx-5)' : color }}>
        {value > 0 ? `${value.toFixed(1)}%` : '—'}
      </span>
    </div>
  );
}

// ── Main Component ─────────────────────────────────────────────────────────────
export function StrategyDashboard({
  onNavigateToFund, onNavigateToFire,
}: {
  onNavigateToFund?: (fundId: string) => void;
  onNavigateToFire?: (fundId: string) => void;
}) {
  const { state, dispatch } = useAppState();
  const { assets, buckets, liabilities = [] } = state;

  const [overviewRows, setOverviewRows]   = useState<FundOverview[]>([]);
  const [viewLoaded,   setViewLoaded]     = useState(false);
  const [addOpen,      setAddOpen]        = useState(false);
  const [editBucket,   setEditBucket]     = useState<Bucket | null>(null);
  const [form,         setForm]           = useState<Partial<Bucket>>(empty());
  const [targetInput,  setTargetInput]    = useState('');
  const [expanded,     setExpanded]       = useState<string | null>(null);

  // Fetch view_strategic_fund_overview
  useEffect(() => {
    (supabase as any).from('view_strategic_fund_overview').select('*')
      .then(({ data, error }: any) => {
        if (error) console.error('[StrategyDashboard] view fetch error:', error.code, error.message);
        if (data && data.length > 0) setOverviewRows(data as FundOverview[]);
        setViewLoaded(true);
      })
      .catch((e: any) => { console.error('[StrategyDashboard] view fetch catch:', e); setViewLoaded(true); });
  }, [buckets.length]);

  const f = (k: keyof Bucket, v: any) => setForm(p => ({ ...p, [k]: v }));
  const openAdd = () => { setForm(empty()); setTargetInput(''); setAddOpen(true); };
  const openEdit = useCallback((b: Bucket) => {
    setForm({ ...b }); setTargetInput(b.targetValue > 0 ? String(b.targetValue) : ''); setEditBucket(b);
  }, []);
  const handleDelete = useCallback((b: Bucket) => {
    if (window.confirm(`Xóa danh mục "${b.name}"? Tài sản sẽ chuyển về "Danh mục Chung".`))
      dispatch({ type: 'DELETE_BUCKET', payload: b.id });
  }, [dispatch]);

  const handleSave = () => {
    if (!form.name) return;
    const payload = { ...form, targetValue: parseFloat(targetInput.replace(/,/g, '')) || 0 } as Bucket;
    if (editBucket) {
      dispatch({ type: 'UPDATE_BUCKET', payload: { ...editBucket, ...payload } as Bucket });
      setEditBucket(null);
    } else {
      dispatch({ type: 'ADD_BUCKET', payload: payload as Omit<Bucket, 'id'> });
      setAddOpen(false);
    }
    setForm(empty());
  };

  // ── Macro KPIs ─────────────────────────────────────────────────────────────
  const totalAssets  = getTotalNAV(assets);
  const totalDebt    = getTotalLiabilities(liabilities);
  const netNAV       = totalAssets - totalDebt;

  // ── Fund groups ──────────────────────────────────────────────────────────
  const realBuckets = buckets.filter(b => b.id !== 'ruong-chua');
  const ruongChua   = buckets.find(b => b.id === 'ruong-chua');

  // Resolve status: ưu tiên view, fallback local
  const statusMap: Record<string, FundStatus> = {};
  overviewRows.forEach(r => { if (r.id) statusMap[r.id] = r.completion_status; });
  const getStatus = (b: Bucket): FundStatus => statusMap[b.id] ?? getStatusFromBucket(b, assets);

  const constructing = realBuckets.filter(b => getStatus(b) === 'constructing')
    .sort((a, b) => {
      const { pct: pa } = getBucketProgress(a, assets);
      const { pct: pb } = getBucketProgress(b, assets);
      return (pa ?? 0) - (pb ?? 0);
    });
  const completed = realBuckets.filter(b => getStatus(b) === 'completed');
  const over      = realBuckets.filter(b => getStatus(b) === 'over');

  // ── Allocation snapshot ────────────────────────────────────────────────────
  const totalTarget = realBuckets.reduce((s, b) => s + b.targetValue, 0);
  const unallocatedNAV = ruongChua
    ? assets.filter(a => a.bucketId === 'ruong-chua' && a.id !== CASH_WALLET_ASSET_ID).reduce((s, a) => s + getAssetCurrentValue(a), 0)
    : 0;

  const fundRows = realBuckets.map((b, i) => {
    const gross = getBucketProgress(b, assets).nav;
    const debt  = liabilities.filter((l: any) => l.fundId === b.id)
                    .reduce((s: number, l: any) => s + l.outstandingBalance, 0);
    const nav   = Math.max(0, gross - debt);
    const weight = netNAV > 0 ? (nav / netNAV) * 100 : 0;
    const gap    = b.targetValue > 0 ? nav - b.targetValue : null;
    const pct    = b.targetValue > 0 ? (nav / b.targetValue) * 100 : null;
    return {
      id: b.id, name: b.name,
      color: b.color || COLORS_FALLBACK[i % COLORS_FALLBACK.length],
      nav, debt, weight, target: b.targetValue, gap, pct,
    };
  });

  return (
    <div style={{ display:'flex', flexDirection:'column', gap:20 }}>

      {/* ── Page header ── */}
      <div style={{ display:'flex', alignItems:'center', justifyContent:'space-between', flexWrap:'wrap', gap:12 }}>
        <div>
          <div style={{ fontSize:18, fontWeight:700, color:'var(--tx-1)', letterSpacing:'-0.02em' }}>Trung tâm Chiến lược Quỹ</div>
          <div style={{ fontSize:12, color:'var(--tx-4)', marginTop:2 }}>Tổng quan phân bổ vốn theo mục tiêu chiến lược dài hạn</div>
        </div>
        <Button variant="primary" size="sm" icon={<Plus size={13}/>} onClick={openAdd}>Tạo Quỹ mới</Button>
      </div>

      {/* ═══════════════════════════════════════════════════════════════════
          TẦNG 1 — Chỉ số vĩ mô
      ═══════════════════════════════════════════════════════════════════ */}
      <div style={{ display:'flex', gap:14, flexWrap:'wrap' }}>
        <KpiCard
          icon={<Wallet size={14} color="#10b981"/>}
          label="Tổng Gia sản"
          value={<Money value={totalAssets} />}
          sub={`${assets.length} tài sản · ${realBuckets.length} quỹ`}
          color="#10b981"
        />
        <KpiCard
          icon={<CreditCard size={14} color={totalDebt > 0 ? '#f87171' : 'var(--tx-4)'}/>}
          label="Tổng Nợ"
          value={<Money value={totalDebt} />}
          sub={totalDebt > 0 ? `Tỷ lệ nợ: ${totalAssets > 0 ? ((totalDebt/totalAssets)*100).toFixed(1) : 0}% tổng tài sản` : 'Không có khoản nợ'}
          color={totalDebt > 0 ? '#f87171' : '#64748b'}
        />
        <KpiCard
          icon={<Activity size={14} color="#38bdf8"/>}
          label="Tài sản Ròng (NAV)"
          value={<Money value={netNAV} />}
          sub={`Mục tiêu tổng: ${formatVND(totalTarget)}`}
          color="#38bdf8"
          accentBg="linear-gradient(135deg,rgba(56,189,248,0.06) 0%,var(--bg-card) 60%)"
        />
      </div>

      {/* ═══════════════════════════════════════════════════════════════════
          TẦNG 2 — Đối soát Phân bổ
      ═══════════════════════════════════════════════════════════════════ */}
      <style>{`
        .alloc-tier2 { display: grid; grid-template-columns: repeat(3, 1fr); gap: 14px; align-items: stretch; }
        .alloc-row {
          display: grid;
          grid-template-columns: 1fr 56px 108px 96px;
          padding: 10px 14px;
          align-items: center;
        }
        .alloc-row-header {
          display: grid;
          grid-template-columns: 1fr 56px 108px 96px;
          padding: 7px 14px;
          border-bottom: 1px solid var(--bd);
          font-size: 10px; font-weight: 700;
          text-transform: uppercase; letter-spacing: 0.06em;
          color: var(--tx-5);
        }
        .alloc-col-target { display: block; }
        @media (max-width: 900px) {
          .alloc-tier2 { grid-template-columns: 1fr; }
        }
        @media (min-width: 901px) and (max-width: 1200px) {
          .alloc-tier2 { grid-template-columns: repeat(2, 1fr); }
        }
        @media (max-width: 420px) {
          .alloc-row, .alloc-row-header { grid-template-columns: 1fr 50px 90px; }
          .alloc-col-target { display: none; }
        }
      `}</style>

      {fundRows.length === 0 ? (
        <Card>
          <CardBody>
            <div style={{ textAlign:'center', color:'var(--tx-4)', padding:'40px 0', fontSize:13 }}>
              Chưa có quỹ nào. Tạo quỹ mới để bắt đầu.
            </div>
          </CardBody>
        </Card>
      ) : (
        <div className="alloc-tier2">

          {/* ── Card 1: Bảng phân bổ ── */}
          <Card style={{ display:'flex', flexDirection:'column' }}>
            <div style={{ padding:'12px 14px', borderBottom:'1px solid var(--bd)' }}>
              <div style={{ fontSize:11, fontWeight:700, color:'var(--tx-1)' }}>Phân bổ Vốn</div>
              <div style={{ fontSize:10, color:'var(--tx-5)', marginTop:1 }}>Tỷ trọng & NAV sau nợ</div>
            </div>
            <CardBody style={{ padding:0 }}>
              <div className="alloc-row-header">
                <span>Quỹ</span>
                <span style={{ textAlign:'right' }}>%</span>
                <span style={{ textAlign:'right' }}>NAV</span>
                <span className="alloc-col-target" style={{ textAlign:'right' }}>Mục tiêu</span>
              </div>
              {fundRows.map((row, i) => (
                <div key={row.id} className="alloc-row" style={{
                  borderBottom: i < fundRows.length - 1 || unallocatedNAV > 0 ? '1px solid var(--bd)' : 'none',
                  cursor: onNavigateToFund ? 'pointer' : undefined,
                }}
                  onClick={() => onNavigateToFund?.(row.id)}
                  onMouseEnter={e => { if (onNavigateToFund) (e.currentTarget as HTMLDivElement).style.background = 'var(--bg-row-hover)'; }}
                  onMouseLeave={e => { (e.currentTarget as HTMLDivElement).style.background = 'transparent'; }}
                >
                  <div style={{ display:'flex', alignItems:'center', gap:7, overflow:'hidden' }}>
                    <div style={{ width:7, height:7, borderRadius:2, backgroundColor:row.color, flexShrink:0 }} />
                    <span style={{ fontSize:12, fontWeight:600, color:'var(--tx-1)', overflow:'hidden', textOverflow:'ellipsis', whiteSpace:'nowrap' }}>{row.name}</span>
                  </div>
                  <div style={{ textAlign:'right', fontSize:12, fontWeight:700, color:'var(--tx-2)' }}>
                    {row.weight.toFixed(1)}%
                  </div>
                  <div style={{ textAlign:'right' }}>
                    <div style={{ fontSize:12, fontWeight:600, color:'var(--tx-1)' }}><Money value={row.nav} /></div>
                    {row.debt > 0 && <div style={{ fontSize:10, color:'#f87171', marginTop:1 }}>−<Money value={row.debt} /></div>}
                  </div>
                  <div className="alloc-col-target" style={{ textAlign:'right' }}>
                    {row.target > 0 ? (
                      <>
                        <div style={{ fontSize:12, color: row.gap !== null && row.gap >= 0 ? '#10b981' : '#f87171' }}>
                          <Money value={row.target} />
                        </div>
                        {row.pct !== null && <div style={{ fontSize:10, color:'var(--tx-5)', marginTop:1 }}>{row.pct.toFixed(0)}%</div>}
                      </>
                    ) : <span style={{ fontSize:11, color:'var(--tx-5)' }}>—</span>}
                  </div>
                </div>
              ))}
              {unallocatedNAV > 0 && (
                <div className="alloc-row" style={{ borderBottom:'1px solid var(--bd)' }}>
                  <div style={{ display:'flex', alignItems:'center', gap:7 }}>
                    <div style={{ width:7, height:7, borderRadius:2, backgroundColor: ruongChua?.color ?? '#64748b', flexShrink:0 }} />
                    <span style={{ fontSize:11, color:'var(--tx-3)' }}>{ruongChua?.name ?? 'Rương chứa'}</span>
                  </div>
                  <div style={{ textAlign:'right', fontSize:11, color:'var(--tx-4)' }}>
                    {netNAV > 0 ? ((unallocatedNAV / netNAV) * 100).toFixed(1) : 0}%
                  </div>
                  <div style={{ textAlign:'right', fontSize:11, color:'var(--tx-4)' }}><Money value={unallocatedNAV} /></div>
                  <div className="alloc-col-target" />
                </div>
              )}
              <div className="alloc-row" style={{ borderTop:'2px solid var(--bd)' }}>
                <span style={{ fontSize:10, fontWeight:700, color:'var(--tx-4)', textTransform:'uppercase', letterSpacing:'0.05em' }}>Tổng</span>
                <div style={{ textAlign:'right', fontSize:11, fontWeight:700, color:'var(--tx-3)' }}>100%</div>
                <div style={{ textAlign:'right', fontSize:12, fontWeight:700, color:'var(--tx-1)' }}><Money value={netNAV} /></div>
                <div className="alloc-col-target" style={{ textAlign:'right', fontSize:11, fontWeight:600, color:'var(--tx-3)' }}>
                  {totalTarget > 0 ? <Money value={totalTarget} /> : <span style={{ color:'var(--tx-5)' }}>—</span>}
                </div>
              </div>
            </CardBody>
          </Card>

          {/* ── Card 2: Pie chart cơ cấu ── */}
          <Card style={{ display:'flex', flexDirection:'column' }}>
            <div style={{ padding:'12px 14px', borderBottom:'1px solid var(--bd)' }}>
              <div style={{ fontSize:11, fontWeight:700, color:'var(--tx-1)' }}>Cơ cấu Tỷ trọng</div>
              <div style={{ fontSize:10, color:'var(--tx-5)', marginTop:1 }}>Theo NAV thực tế</div>
            </div>
            <CardBody style={{ flex:1, padding:'16px 14px', display:'flex', flexDirection:'column', alignItems:'center', justifyContent:'center', gap:14 }}>
              <div style={{ position:'relative', width:180, height:180, flexShrink:0 }}>
                <ResponsiveContainer width="100%" height="100%">
                  <PieChart>
                    <Pie
                      data={[
                        ...fundRows.map(r => ({ name: r.name, value: r.nav, color: r.color, id: r.id })),
                        ...(unallocatedNAV > 0 ? [{ name: ruongChua?.name ?? 'Rương chứa', value: unallocatedNAV, color: ruongChua?.color ?? '#64748b', id: null }] : []),
                      ]}
                      cx="50%" cy="50%" innerRadius={50} outerRadius={80}
                      paddingAngle={2} dataKey="value"
                      stroke="var(--bg-card)" strokeWidth={2}
                      style={{ cursor: onNavigateToFund ? 'pointer' : undefined }}
                      onClick={(data: any) => { if (data?.id) onNavigateToFund?.(data.id); }}
                    >
                      {[...fundRows, ...(unallocatedNAV > 0 ? [{ color: ruongChua?.color ?? '#64748b' }] : [])].map((r, i) => (
                        <Cell key={i} fill={r.color} />
                      ))}
                    </Pie>
                    <Tooltip
                      content={({ active, payload }: any) => {
                        if (!active || !payload?.length) return null;
                        const d = payload[0];
                        const pct = netNAV > 0 ? ((d.value / netNAV) * 100).toFixed(1) : '0';
                        return (
                          <div style={{ background:'var(--bg-card)', border:'1px solid var(--bd)', borderRadius:8, padding:'8px 12px', fontSize:11 }}>
                            <div style={{ fontWeight:700, color:d.payload.color, marginBottom:3 }}>{d.name}</div>
                            <div style={{ color:'var(--tx-1)', fontWeight:700 }}><Money value={d.value} /></div>
                            <div style={{ color:'var(--tx-4)' }}>{pct}%</div>
                          </div>
                        );
                      }}
                      wrapperStyle={{ zIndex:100 }}
                    />
                  </PieChart>
                </ResponsiveContainer>
                <div style={{ position:'absolute', top:'50%', left:'50%', transform:'translate(-50%,-50%)', textAlign:'center', pointerEvents:'none' }}>
                  <div style={{ fontSize:9, color:'var(--tx-5)', marginBottom:2 }}>NAV ròng</div>
                  <div style={{ fontSize:12, fontWeight:700, color:'var(--tx-1)', letterSpacing:'-0.02em' }}><Money value={netNAV} /></div>
                </div>
              </div>
              <div style={{ display:'flex', flexDirection:'column', gap:5, width:'100%' }}>
                {fundRows.map((r, i) => (
                  <div key={i} style={{ display:'flex', alignItems:'center', gap:7, cursor: onNavigateToFund ? 'pointer' : undefined, borderRadius:4, padding:'2px 4px', margin:'-2px -4px' }}
                    onClick={() => onNavigateToFund?.(r.id)}
                    onMouseEnter={e => { if (onNavigateToFund) (e.currentTarget as HTMLDivElement).style.background = 'var(--bg-row-hover)'; }}
                    onMouseLeave={e => { (e.currentTarget as HTMLDivElement).style.background = 'transparent'; }}
                  >
                    <div style={{ width:7, height:7, borderRadius:2, backgroundColor:r.color, flexShrink:0 }} />
                    <span style={{ fontSize:11, color:'var(--tx-3)', flex:1, overflow:'hidden', textOverflow:'ellipsis', whiteSpace:'nowrap' }}>{r.name}</span>
                    <span style={{ fontSize:11, fontWeight:600, color:'var(--tx-2)' }}>{r.weight.toFixed(1)}%</span>
                  </div>
                ))}
                {unallocatedNAV > 0 && (
                  <div style={{ display:'flex', alignItems:'center', gap:7 }}>
                    <div style={{ width:7, height:7, borderRadius:2, backgroundColor: ruongChua?.color ?? '#64748b', flexShrink:0 }} />
                    <span style={{ fontSize:11, color:'var(--tx-3)', flex:1 }}>{ruongChua?.name ?? 'Rương chứa'}</span>
                    <span style={{ fontSize:11, color:'var(--tx-4)' }}>{netNAV > 0 ? ((unallocatedNAV / netNAV) * 100).toFixed(1) : 0}%</span>
                  </div>
                )}
              </div>
            </CardBody>
          </Card>

          {/* ── Card 3: Treemap tiến độ ── */}
          <Card style={{ display:'flex', flexDirection:'column' }}>
            <div style={{ padding:'12px 14px', borderBottom:'1px solid var(--bd)' }}>
              <div style={{ fontSize:11, fontWeight:700, color:'var(--tx-1)' }}>Tiến độ Mục tiêu</div>
              <div style={{ fontSize:10, color:'var(--tx-5)', marginTop:1 }}>Diện tích ~ NAV · Màu ~ % hoàn thành</div>
            </div>
            <CardBody style={{ flex:1, padding:'14px', display:'flex', flexDirection:'column' }}>
              <div style={{ width:'100%', flex:1, minHeight:160 }}>
                <ResponsiveContainer width="100%" height="100%">
                  <Treemap
                    data={fundRows.map(r => ({
                      name: r.name,
                      size: Math.max(r.nav, 1),
                      pct: r.pct,
                      id: r.id,
                      fill: r.pct === null ? r.color
                        : r.pct >= 100 ? '#10b981'
                        : r.pct >= 75  ? '#84cc16'
                        : r.pct >= 50  ? '#fbbf24'
                        : r.pct >= 25  ? '#f97316'
                        : '#f87171',
                    }))}
                    dataKey="size"
                    onClick={(data: any) => { if (data?.id) onNavigateToFund?.(data.id); }}
                    content={((props: any) => {
                      const { x, y, width, height, name, fill, id } = props;
                      const pct: number | null | undefined = props.pct;
                      if (!fill || !width || !height || width < 4 || height < 4) return null;
                      const showPct = pct != null && width > 50 && height > 40;
                      return (
                        <g style={{ cursor: id && onNavigateToFund ? 'pointer' : undefined }}>
                          <rect x={x + 1} y={y + 1} width={width - 2} height={height - 2}
                            fill={fill} rx={4} opacity={0.88} />
                          {width > 50 && height > 24 && (
                            <text x={x + width / 2} y={y + height / 2 - (showPct ? 8 : 0)}
                              textAnchor="middle" dominantBaseline="middle"
                              fill="white" fontSize={11} fontWeight={600}
                              style={{ pointerEvents:'none' }}>
                              {name.length > 14 ? name.slice(0, 13) + '…' : name}
                            </text>
                          )}
                          {showPct && (
                            <text x={x + width / 2} y={y + height / 2 + 10}
                              textAnchor="middle" dominantBaseline="middle"
                              fill="rgba(255,255,255,0.85)" fontSize={10}
                              style={{ pointerEvents:'none' }}>
                              {(pct as number).toFixed(0)}%
                            </text>
                          )}
                        </g>
                      );
                    }) as any}
                  />
                </ResponsiveContainer>
              </div>
              {/* Color scale legend */}
              <div style={{ display:'flex', gap:8, marginTop:10, flexWrap:'wrap' }}>
                {[
                  { color:'#10b981', label:'≥100%' },
                  { color:'#84cc16', label:'≥75%' },
                  { color:'#fbbf24', label:'≥50%' },
                  { color:'#f97316', label:'≥25%' },
                  { color:'#f87171', label:'<25%' },
                ].map(({ color, label }) => (
                  <div key={label} style={{ display:'flex', alignItems:'center', gap:4 }}>
                    <div style={{ width:8, height:8, borderRadius:2, backgroundColor:color }} />
                    <span style={{ fontSize:10, color:'var(--tx-5)' }}>{label}</span>
                  </div>
                ))}
              </div>
            </CardBody>
          </Card>

        </div>
      )}

      {/* ═══════════════════════════════════════════════════════════════════
          TẦNG 3 — Kanban 3 cột
      ═══════════════════════════════════════════════════════════════════ */}
      <style>{`
        .strategy-kanban {
          display: grid;
          grid-template-columns: repeat(3, 1fr);
          gap: 16px;
          align-items: start;
        }
        @media (max-width: 900px) {
          .strategy-kanban { grid-template-columns: 1fr; }
        }
        @media (min-width: 901px) and (max-width: 1200px) {
          .strategy-kanban { grid-template-columns: repeat(2, 1fr); }
        }
      `}</style>
      <div className="strategy-kanban">
        <KanbanColumn
          label="Đang xây dựng"
          color="#fbbf24"
          bgOverlay="rgba(251,191,36,0.03)"
          borderColor="rgba(251,191,36,0.14)"
          icon={<TrendingUp size={13} color="#fbbf24"/>}
          buckets={constructing} status="constructing"
          assets={assets} liabilities={liabilities} totalNAV={totalAssets} overviewRows={overviewRows}
          onNavigateToFund={onNavigateToFund} onNavigateToFire={onNavigateToFire}
          onEdit={openEdit} onDelete={handleDelete} expanded={expanded} setExpanded={setExpanded}
        />
        <KanbanColumn
          label="Đạt mục tiêu"
          color="#10b981"
          bgOverlay="rgba(16,185,129,0.03)"
          borderColor="rgba(16,185,129,0.14)"
          icon={<Check size={13} color="#10b981"/>}
          buckets={completed} status="completed"
          assets={assets} liabilities={liabilities} totalNAV={totalAssets} overviewRows={overviewRows}
          onNavigateToFund={onNavigateToFund} onNavigateToFire={onNavigateToFire}
          onEdit={openEdit} onDelete={handleDelete} expanded={expanded} setExpanded={setExpanded}
        />
        <KanbanColumn
          label="Vượt mục tiêu"
          color="#a78bfa"
          bgOverlay="rgba(167,139,250,0.03)"
          borderColor="rgba(167,139,250,0.14)"
          icon={<Star size={13} color="#a78bfa"/>}
          buckets={over} status="over"
          assets={assets} liabilities={liabilities} totalNAV={totalAssets} overviewRows={overviewRows}
          onNavigateToFund={onNavigateToFund} onNavigateToFire={onNavigateToFire}
          onEdit={openEdit} onDelete={handleDelete} expanded={expanded} setExpanded={setExpanded}
        />
      </div>

      {/* Rương chứa — compact */}
      {ruongChua && (() => {
        const rcNAV = assets.filter(a => a.bucketId === 'ruong-chua' && a.id !== CASH_WALLET_ASSET_ID).reduce((s, a) => s + getAssetCurrentValue(a), 0);
        const rcAssets = assets.filter(a => a.bucketId === 'ruong-chua' && a.id !== CASH_WALLET_ASSET_ID);
        const rcExpanded = expanded === ruongChua.id;
        return rcNAV > 0 ? (
          <div>
            <div style={{ fontSize:10, fontWeight:700, color:'var(--tx-5)', textTransform:'uppercase', letterSpacing:'0.07em', marginBottom:8 }}>Danh mục mặc định</div>
            <Card>
              <div style={{ padding:'12px 20px', display:'flex', alignItems:'center', justifyContent:'space-between', cursor:'pointer' }}
                onClick={() => onNavigateToFund?.(ruongChua.id)}>
                <div style={{ display:'flex', alignItems:'center', gap:10 }}>
                  <span style={{ fontSize:12, fontWeight:600, color:'var(--tx-3)' }}>{ruongChua.name}</span>
                  <span style={{ fontSize:10, padding:'1px 6px', borderRadius:4, background:'var(--bg-card-el)', color:'var(--tx-4)' }}>Tự động</span>
                </div>
                <div style={{ display:'flex', alignItems:'center', gap:14 }}>
                  <span style={{ fontSize:13, fontWeight:700, color:'var(--tx-2)' }}><Money value={rcNAV} /></span>
                  <button onClick={e=>{e.stopPropagation(); setExpanded(rcExpanded ? null : ruongChua.id);}}
                    style={{ background:'none', border:'none', cursor:'pointer', color:'var(--tx-4)', display:'flex' }}>
                    {rcExpanded ? <ChevronUp size={14}/> : <ChevronDown size={14}/>}
                  </button>
                </div>
              </div>
              {rcExpanded && (
                <div style={{ borderTop:'1px solid var(--bd-sub)' }} onClick={e=>e.stopPropagation()}>
                  {rcAssets.map((a, i) => (
                    <div key={a.id} style={{ display:'flex', alignItems:'center', justifyContent:'space-between', padding:'8px 20px', borderBottom: i < rcAssets.length-1 ? '1px solid var(--bd-sub)' : 'none' }}>
                      <div style={{ display:'flex', alignItems:'center', gap:8 }}>
                        <div style={{ width:3, height:3, borderRadius:'50%', backgroundColor:'var(--tx-4)' }} />
                        <span style={{ fontSize:12, fontWeight:600, color:'var(--tx-2)' }}>{a.name}</span>
                      </div>
                      <span style={{ fontSize:12, color:'var(--tx-3)' }}><Money value={getAssetCurrentValue(a)} /></span>
                    </div>
                  ))}
                </div>
              )}
            </Card>
          </div>
        ) : null;
      })()}

      {realBuckets.length === 0 && !viewLoaded && (
        <div style={{ textAlign:'center', padding:'64px 0', color:'var(--tx-4)', fontSize:13 }}>Đang tải dữ liệu...</div>
      )}
      {realBuckets.length === 0 && viewLoaded && (
        <div style={{ textAlign:'center', padding:'64px 0', border:'1.5px dashed var(--bd)', borderRadius:16 }}>
          <div style={{ fontSize:14, fontWeight:600, color:'var(--tx-3)', marginBottom:8 }}>Chưa có quỹ nào</div>
          <Button variant="primary" size="sm" icon={<Plus size={13}/>} onClick={openAdd}>Tạo Quỹ đầu tiên</Button>
        </div>
      )}

      {/* ── Modal thêm/sửa quỹ ── */}
      <Modal open={addOpen || editBucket !== null}
        onClose={() => { setAddOpen(false); setEditBucket(null); setForm(empty()); }}
        title={editBucket ? 'Chỉnh sửa Quỹ' : 'Tạo Quỹ mới'} size="sm">
        <div style={{ display:'flex', flexDirection:'column', gap:14 }}>
          <Input label="Tên quỹ *" value={form.name||''} onChange={e=>f('name',e.target.value)} placeholder="VD: Quỹ FIRE" />
          <Textarea label="Mô tả (sứ mệnh quỹ)" value={form.description||''} onChange={e=>f('description',e.target.value)} placeholder="VD: Dự phòng khẩn cấp 8-12 tháng chi phí" />
          <div>
            <div style={{ fontSize:11, color:'var(--tx-4)', fontWeight:500, letterSpacing:'0.04em', textTransform:'uppercase', marginBottom:6 }}>Giá trị mục tiêu (VNĐ)</div>
            <input type="number" value={targetInput} onChange={e=>setTargetInput(e.target.value)}
              placeholder="VD: 40000000000 (= 40.00 tỷ)"
              style={{ width:'100%', padding:'9px 12px', borderRadius:9, fontSize:13, outline:'none', backgroundColor:'var(--bg-input)', border:'1px solid var(--bd)', color:'var(--tx-1)', boxSizing:'border-box' }} />
            {targetInput && parseFloat(targetInput) > 0 && (
              <div style={{ fontSize:11, color:'#10b981', marginTop:4 }}>= {formatVND(parseFloat(targetInput))}</div>
            )}
          </div>
          <div>
            <div style={{ fontSize:11, color:'var(--tx-4)', fontWeight:500, letterSpacing:'0.04em', textTransform:'uppercase', marginBottom:8 }}>Màu sắc</div>
            <div style={{ display:'flex', gap:8, flexWrap:'wrap' }}>
              {BUCKET_FORM_COLORS.map(c => (
                <button key={c} onClick={()=>f('color',c)}
                  style={{ width:28, height:28, borderRadius:8, cursor:'pointer', background:c, border:`2px solid ${form.color===c?'#fff':'transparent'}`, transform:form.color===c?'scale(1.2)':'scale(1)', transition:'all 0.15s' }} />
              ))}
            </div>
          </div>
          <div style={{ display:'flex', justifyContent:'flex-end', gap:10, paddingTop:14, borderTop:'1px solid var(--bd)' }}>
            <Button variant="ghost" onClick={()=>{ setAddOpen(false); setEditBucket(null); }}>Hủy</Button>
            <Button variant="primary" onClick={handleSave}>{editBucket ? 'Lưu thay đổi' : 'Tạo Quỹ'}</Button>
          </div>
        </div>
      </Modal>
    </div>
  );
}
